import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileText } from "lucide-react";

const NarrativeWriting = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 rtl">
      {/* Header */}
      <div className="relative overflow-hidden min-h-[40vh] flex items-center">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-100 via-cyan-200 to-teal-200"></div>
          <div className="absolute inset-0 opacity-25">
            <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-br from-cyan-200/30 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-teal-200/25 to-transparent rounded-full blur-3xl"></div>
          </div>
        </div>
        
        <div className="relative container mx-auto px-6 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <Button 
              onClick={() => navigate('/')} 
              className="mb-8 bg-blue-600/80 hover:bg-blue-700/80 text-white border border-blue-500/50 font-medium px-6 py-3 rounded-xl shadow-lg"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              חזרה לדף הבית
            </Button>
            
            <h1 className="text-4xl md:text-6xl font-bold text-blue-900 leading-tight mb-6">
              כתיבה נרטיבית לארגונים
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-16">
        <Card className="max-w-4xl mx-auto bg-white/80 backdrop-blur-md border border-white/50 shadow-2xl rounded-3xl">
          <CardContent className="p-8">
            <div className="text-right text-lg leading-relaxed text-slate-700 font-rounded" style={{fontFamily: 'system-ui, -apple-system, sans-serif'}}>
              <p className="mb-8">
                דף זה מציג דוגמאות של כתיבה נרטיבית לארגונים, לקהילות וליוזמות. הכתיבה הנרטיבית היא תרגום רעיון לסיפור אנושי, בדרך שהקורא ירצה להמשיך לקרוא. כתיבה כזו מאפשרת לארגונים להפוך מידע מורכב למסרים בהירים המעוררים רגש ומחדדים את הזהות והחזון של הארגון. שמות הארגונים המוצגים בסעיף זה, לא פורסמו, לשמירה על פרטיות.
              </p>
              
              {/* PDF Document Card - Purple Theme */}
              <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-6 mt-6">
                <p className="text-right text-base leading-relaxed text-purple-900 font-medium mb-4">
                  מסמך המציג עקרונות לכתיבה נרטיבית לארגונים, ואת האופן בו הופך סיפור ארגוני לסיפור אנושי מעניין (דצמבר, 2025).
                </p>
                
                {/* PDF View Button */}
                <div className="flex justify-center mt-4">
                  <a
                    href="https://drive.google.com/file/d/1FiokUKDdSZDPw8VpfEzbXgiULUmp6FSN/preview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
                  >
                    <FileText className="w-5 h-5" />
                    <span className="text-lg">לצפייה בתוצר (PDF)</span>
                  </a>
                </div>
              </div>
              
              {/* Second PDF Document Card - Pink Theme */}
              <div className="bg-pink-50 border-2 border-pink-200 rounded-2xl p-6 mt-6">
                <p className="text-right text-base leading-relaxed text-pink-900 font-medium mb-4">
                  דוגמה לכתיבה נרטיבית לארגון טכנולוגי, במטרה לזקק זהות, ערכים וסיפור ארגוני (יולי, 2025).
                </p>
                
                {/* PDF View Button */}
                <div className="flex justify-center mt-4">
                  <a
                    href="https://drive.google.com/file/d/1dXXAiorQARcIOLHyGLjL7rDlMpKvb5JC/preview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
                  >
                    <FileText className="w-5 h-5" />
                    <span className="text-lg">לצפייה בתוצר (PDF)</span>
                  </a>
                </div>
              </div>
              
              {/* Third PDF Document Card - Green Theme */}
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 mt-6">
                <p className="text-right text-base leading-relaxed text-green-900 font-medium mb-4">
                  דוגמה לכתיבה נרטיבית לעמותה קהילתית, היוצרת סיפור המחבר בין עשייה, קהילה וחזון (נובמבר, 2025).
                </p>
                
                {/* PDF View Button */}
                <div className="flex justify-center mt-4">
                  <a
                    href="https://drive.google.com/file/d/1YViYFs4etPtZ6C3Vc8S4u5yziYfOocfx/preview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
                  >
                    <FileText className="w-5 h-5" />
                    <span className="text-lg">לצפייה בתוצר (PDF)</span>
                  </a>
                </div>
              </div>
              
              {/* Fourth PDF Document Card - Indigo Theme */}
              <div className="bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-6 mt-6">
                <p className="text-right text-base leading-relaxed text-indigo-900 font-medium mb-4">
                  דוגמה לכתיבה נרטיבית לתערוכה, במטרה ליצור חוויה באמצעות סיפור, קשר ומשמעות (ינואר, 2025).
                </p>
                
                {/* PDF View Button */}
                <div className="flex justify-center mt-4">
                  <a
                    href="https://drive.google.com/file/d/1VLl6e253z227CfkyXoaJJ1ytBbgkvCV6/preview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
                  >
                    <FileText className="w-5 h-5" />
                    <span className="text-lg">לצפייה בתוצר (PDF)</span>
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NarrativeWriting;